class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('.js-facets-wrapper-desktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false, FacetFiltersForm.popstateConfig());
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static configFromScope(scope) {
    if (!scope) {
      return {
        root: document,
        fetchPath: window.location.pathname,
        gridContainerId: 'ProductGridContainer',
        productGridId: 'product-grid',
        productCountId: 'ProductCount',
        productCountDesktopId: 'ProductCountDesktop',
        formDesktopId: 'FacetFiltersForm',
        formMobileId: 'FacetFiltersFormMobile',
        formPillsId: 'FacetFiltersPillsForm',
        formSortDrawerId: 'FacetSortDrawerForm',
        formSortFormId: 'FacetSortForm',
      };
    }
    return {
      root: scope,
      fetchPath: scope.dataset.facetFetchPath || window.location.pathname,
      gridContainerId: scope.dataset.facetGridContainerId || 'ProductGridContainer',
      productGridId: scope.dataset.facetProductGridId || 'product-grid',
      productCountId: scope.dataset.facetProductCountId || 'ProductCount',
      productCountDesktopId: scope.dataset.facetProductCountDesktopId || 'ProductCountDesktop',
      formDesktopId: scope.dataset.facetFormDesktopId || 'FacetFiltersForm',
      formMobileId: scope.dataset.facetFormMobileId || 'FacetFiltersFormMobile',
      formPillsId: scope.dataset.facetFormPillsId || 'FacetFiltersPillsForm',
      formSortDrawerId: scope.dataset.facetFormSortDrawerId || 'FacetSortDrawerForm',
      formSortFormId: scope.dataset.facetSortFormId || 'FacetSortForm',
    };
  }

  static resolveConfigFromEvent(event) {
    const el = event?.target?.closest?.('facet-filters-form');
    if (el) {
      const scope = el.closest('[data-facet-scope]');
      return FacetFiltersForm.configFromScope(scope);
    }
    return FacetFiltersForm.popstateConfig();
  }

  static popstateConfig() {
    const pg = document.getElementById('product-grid');
    const scope = pg?.closest('[data-facet-scope]');
    return FacetFiltersForm.configFromScope(scope);
  }

  static toggleActiveFacets(disable = true, root = document) {
    root.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, config) {
    const cfg = config || FacetFiltersForm.resolveConfigFromEvent(event);
    FacetFiltersForm.activeConfig = cfg;
    FacetFiltersForm.searchParamsPrev = searchParams;

    const productGridEl =
      document.getElementById(cfg.productGridId) ||
      cfg.root.querySelector('#product-grid') ||
      document.getElementById('product-grid');
    if (!productGridEl?.dataset?.id) return;

    const sections = [{ section: productGridEl.dataset.id }];

    const countContainer = document.getElementById(cfg.productCountId);
    const countContainerDesktop = document.getElementById(cfg.productCountDesktopId);
    const loadingSpinners = cfg.root.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const gridHost = document.getElementById(cfg.gridContainerId);
    const collectionEl = gridHost?.querySelector('.collection');
    if (collectionEl) collectionEl.classList.add('loading');

    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${cfg.fetchPath}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, cfg)
        : FacetFiltersForm.renderSectionFromFetch(url, event, cfg);
    });

    if (updateURLHash && cfg.fetchPath === window.location.pathname) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static renderSectionFromFetch(url, event, cfg) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event, cfg);
        FacetFiltersForm.renderProductGridContainer(html, cfg);
        FacetFiltersForm.renderProductCount(html, cfg);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, cfg) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event, cfg);
    FacetFiltersForm.renderProductGridContainer(html, cfg);
    FacetFiltersForm.renderProductCount(html, cfg);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html, cfg) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.getElementById(cfg.gridContainerId);
    const target = document.getElementById(cfg.gridContainerId);
    if (!source || !target) return;

    target.innerHTML = source.innerHTML;

    target.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html, cfg) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsed.getElementById(cfg.productCountId);
    const container = document.getElementById(cfg.productCountId);
    const containerDesktop = document.getElementById(cfg.productCountDesktopId);

    if (sourceCount && container) {
      container.innerHTML = sourceCount.innerHTML;
      container.classList.remove('loading');
    }

    const sourceCountDesktop = parsed.getElementById(cfg.productCountDesktopId);
    if (sourceCountDesktop && containerDesktop) {
      containerDesktop.innerHTML = sourceCountDesktop.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = cfg.root.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event, cfg) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const desktopForm = parsedHTML.getElementById(cfg.formDesktopId);
    const mobileForm = parsedHTML.getElementById(cfg.formMobileId);
    const pillsForm = parsedHTML.getElementById(cfg.formPillsId);

    const fromForm = (form) => (form ? Array.from(form.querySelectorAll('.js-filter')) : []);

    const facetDetailsElementsFromFetch = [
      ...fromForm(desktopForm),
      ...fromForm(mobileForm),
      ...fromForm(pillsForm),
    ];

    const domDesktop = document.getElementById(cfg.formDesktopId);
    const domMobile = document.getElementById(cfg.formMobileId);
    const domPills = document.getElementById(cfg.formPillsId);
    const facetDetailsElementsFromDom = [...fromForm(domDesktop), ...fromForm(domMobile), ...fromForm(domPills)];

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement?.id) {
          const parent = document.getElementById(elementToRender.parentElement.id);
          const firstFilter = parent?.querySelector('.js-filter');
          if (firstFilter) firstFilter.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML, cfg);
    FacetFiltersForm.renderAdditionalElements(parsedHTML, cfg);

    if (countsToRender && event?.target) {
      const jsFilter = event.target.closest('.js-filter');
      const closestJSFilterID = jsFilter?.id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, jsFilter);
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html, cfg) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = cfg.root.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false, cfg.root);
  }

  static renderAdditionalElements(html, cfg) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = cfg.root.querySelector(selector);
      if (target) target.innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileForm = document.getElementById(cfg.formMobileId);
    const drawer = mobileForm?.closest('menu-drawer');
    if (drawer?.bindEvents) drawer.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    const scope = this.closest('[data-facet-scope]');
    const cfg = FacetFiltersForm.configFromScope(scope);
    FacetFiltersForm.renderPage(searchParams, event, true, cfg);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const scope = this.closest('[data-facet-scope]');
    const root = scope || document;
    const sortFilterForms = root.querySelectorAll('facet-filters-form form');

    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const cfg = FacetFiltersForm.configFromScope(scope);
      const isMobile = event.target.closest('form').id === cfg.formMobileId;

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === cfg.formSortFormId ||
            form.id === cfg.formDesktopId ||
            form.id === cfg.formSortDrawerId
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === cfg.formMobileId) {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const scope = this.closest('[data-facet-scope]');
    const cfg = FacetFiltersForm.configFromScope(scope);
    FacetFiltersForm.toggleActiveFacets(true, cfg.root);
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, null, true, cfg);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.activeConfig = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form');
    if (form) form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
